/**
 * Server-minus-phone clock offset, measured from the hosting response `Date` header
 * (pages are served no-store, so it's fresh). A worker who moves the phone clock while
 * online gets the real time anyway; offline, the last offset measured is the best we
 * have, and the check-in is marked `offline` for the office's integrity flags.
 */
const KEY = 'server-clock-offset';

export interface ClockReading {
  /** Server minus phone, ms. null = never measured on this device. */
  offsetMs: number | null;
  /** True when this reading was just measured against the server. */
  fresh: boolean;
}

function stored(): number | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

/** Measure now (≤ timeoutMs); falls back to the last stored offset. */
export async function readServerClock(timeoutMs = 3000): Promise<ClockReading> {
  if (!navigator.onLine) return { offsetMs: stored(), fresh: false };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const sent = Date.now();
    // Not precached and not a navigation, so the service worker passes it to the network.
    const res = await fetch(`/time-probe?t=${sent}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: ctrl.signal,
    });
    const received = Date.now();
    const date = res.headers.get('date');
    if (!date) throw new Error('no Date header');
    const age = Number(res.headers.get('age') ?? 0) * 1000;
    // Date has 1s resolution; +500ms centres it. Midpoint of the round trip ≈ server instant.
    const serverNow = Date.parse(date) + 500 + age;
    const offsetMs = Math.round(serverNow - (sent + received) / 2);
    try {
      localStorage.setItem(KEY, String(offsetMs));
    } catch {
      /* private mode — still use it for this event */
    }
    return { offsetMs, fresh: true };
  } catch {
    return { offsetMs: stored(), fresh: false };
  } finally {
    clearTimeout(timer);
  }
}
